import { api } from '../lib/api';
import type { BrandingSettings, BrandTheme, DomainStage } from '../lib/types';

/**
 * The team's logos, color, slates and their own domain.
 *
 * Every call here answers with the whole settings object, so the page never
 * has to merge a partial response into what it was already showing.
 */

export function fetchBranding(orgId: string): Promise<BrandingSettings> {
  return api<BrandingSettings>(`/v1/orgs/${orgId}/branding`);
}

export interface BrandingUpdate {
  /** `null` clears the color; omitted leaves it alone. */
  brandColor?: string | null;
  introVideoId?: string | null;
  outroVideoId?: string | null;
  theme?: BrandTheme;
  /** Take the credit line and the signup link off their share pages. */
  hideSlateBranding?: boolean;
}

export function updateBranding(orgId: string, body: BrandingUpdate): Promise<BrandingSettings> {
  return api<BrandingSettings>(`/v1/orgs/${orgId}/branding`, { method: 'PUT', body });
}

export type LogoKind = 'square' | 'wide';

/**
 * A logo, sent as base64 in JSON like a profile picture. The file is read
 * here rather than in the component so every caller gets the same size and
 * type guard.
 */
export async function uploadLogo(orgId: string, kind: LogoKind, file: File): Promise<BrandingSettings> {
  const data = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      // "data:image/png;base64,AAAA" — the API wants only what follows.
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(new Error('That file could not be read.'));
    reader.readAsDataURL(file);
  });
  return api<BrandingSettings>(`/v1/orgs/${orgId}/branding/logo`, {
    method: 'PUT',
    body: { kind, contentType: file.type, data },
  });
}

export function removeLogo(orgId: string, kind: LogoKind): Promise<BrandingSettings> {
  return api<BrandingSettings>(`/v1/orgs/${orgId}/branding/logo?kind=${kind}`, { method: 'DELETE' });
}

/** Claim a hostname and start the walk towards serving links from it. */
export function setCustomDomain(orgId: string, host: string): Promise<BrandingSettings> {
  return api<BrandingSettings>(`/v1/orgs/${orgId}/branding/domain`, { method: 'PUT', body: { host } });
}

/** "Check again": asks AWS and DNS right now rather than on the next poll. */
export function checkCustomDomain(orgId: string): Promise<BrandingSettings> {
  return api<BrandingSettings>(`/v1/orgs/${orgId}/branding/domain/verify`, { method: 'POST' });
}

export function removeCustomDomain(orgId: string): Promise<BrandingSettings> {
  return api<BrandingSettings>(`/v1/orgs/${orgId}/branding/domain`, { method: 'DELETE' });
}

/** What the images are allowed to be, mirroring the API's own list. */
export const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
export const LOGO_MAX_BYTES = 2 * 1024 * 1024;

/**
 * Black or white, whichever can be read on this color.
 *
 * The API computes the same thing and stores it with the color; this copy
 * exists for the preview, which follows what is being typed rather than what
 * has been saved. Kept deliberately identical to `readableInkOn` in
 * slate-api/src/policy/branding.ts, which is the authority.
 */
export function inkFor(value: string): string {
  const hex = value.replace('#', '').trim();
  const full =
    hex.length === 3
      ? hex
          .split('')
          .map((c) => c + c)
          .join('')
      : hex;
  if (full.length !== 6) return '#ffffff';
  const channel = (pair: string): number => {
    const srgb = Number.parseInt(pair, 16) / 255;
    return srgb <= 0.03928 ? srgb / 12.92 : ((srgb + 0.055) / 1.055) ** 2.4;
  };
  const luminance =
    0.2126 * channel(full.slice(0, 2)) + 0.7152 * channel(full.slice(2, 4)) + 0.0722 * channel(full.slice(4, 6));
  return luminance > 0.45 ? '#000000' : '#ffffff';
}

/** Why this file will not do, or null. Checked here so the API is not asked. */
export function logoProblem(file: File): string | null {
  if (!LOGO_TYPES.includes(file.type)) return 'Use a PNG, JPEG, WebP or SVG image.';
  if (file.size > LOGO_MAX_BYTES) return 'Logos are limited to 2 MB.';
  return null;
}

/**
 * What the domain is doing, said to the person waiting on it, and always from
 * their point of view: whether the next move is theirs or ours.
 */
export function domainLabel(stage: DomainStage): { title: string; tone: 'info' | 'caution' | 'go' | 'danger' } {
  switch (stage) {
    case 'requesting':
      return { title: 'Preparing your DNS record', tone: 'info' };
    case 'dns':
      return { title: 'Waiting for your DNS record', tone: 'caution' };
    case 'building':
      return { title: 'Building the certificate and CDN', tone: 'info' };
    case 'live':
      return { title: 'Serving your links', tone: 'go' };
    case 'failed':
      return { title: 'Could not be set up', tone: 'danger' };
    default:
      return { title: 'No domain yet', tone: 'info' };
  }
}

/** Is this one the settings page should keep asking about? */
export function domainInFlight(stage: DomainStage): boolean {
  return stage === 'requesting' || stage === 'dns' || stage === 'building';
}

/**
 * How often to ask again. `requesting` is ours and usually resolves in a
 * second or two, so it is worth watching closely; the others wait on a DNS
 * change or on AWS, neither of which rewards impatience.
 */
export function domainPollMs(stage: DomainStage): number {
  return stage === 'requesting' ? 3_000 : 15_000;
}
